import { Box, ButtonBase, LinearProgress, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import type { Tier } from "../../../services/referrals";
import i18n from "../../../i18n";

interface HeroBalanceCardProps {
  balance: string;
  minPayoutUsdt: string;
  currentTier: Tier;
  nextTier: Tier | null;
  referralsTotal: number;
  isPremium: boolean;
  onWithdraw: () => void;
}

function formatUsdt(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  const locale = i18n.language === "en" ? "en-US" : "ru-RU";
  return n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Плоская «баннер»-карточка в стиле остальных блоков приложения:
// secondary.main фон, оранжевый акцент только на кнопке и chip премиума.
// Никаких градиентов и цветных фонов — консистентно с /home.
export function HeroBalanceCard({
  balance,
  minPayoutUsdt,
  currentTier,
  nextTier,
  referralsTotal,
  isPremium,
  onWithdraw,
}: HeroBalanceCardProps) {
  const { t } = useTranslation("referral");
  const canWithdraw = Number(balance) >= Number(minPayoutUsdt);

  const progressToNext = nextTier
    ? Math.min(1, referralsTotal / Math.max(1, nextTier.min_referrals))
    : 1;

  const remaining = nextTier
    ? Math.max(0, nextTier.min_referrals - referralsTotal)
    : 0;

  return (
    <Box
      sx={{
        marginTop: "12px",
        borderRadius: "20px",
        padding: "18px",
        backgroundColor: "secondary.main",
        color: "text.primary",
      }}
    >
      {/* Верхняя строка: баланс слева, chip тира справа */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.4px",
              wordBreak: "break-word",
            }}
          >
            {formatUsdt(balance)}
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                marginLeft: "6px",
                opacity: 0.55,
              }}
            >
              USDT
            </Typography>
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              opacity: 0.55,
              marginTop: "6px",
            }}
          >
            {t("hero.min_payout", { amount: formatUsdt(minPayoutUsdt) })}
          </Typography>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            padding: "5px 10px",
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.3px",
            marginTop: "4px",
            backgroundColor: isPremium
              ? "rgba(51, 119, 255, 0.14)"
              : (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.05)",
            color: isPremium ? "primary.main" : "text.secondary",
          }}
        >
          {isPremium && (
            <Icon icon="solar:crown-bold" width={12} height={12} />
          )}
          {currentTier.name}
        </Box>
      </Box>

      {/* Кнопка «Вывести» */}
      <ButtonBase
        onClick={onWithdraw}
        disabled={!canWithdraw}
        sx={{
          marginTop: "16px",
          width: "100%",
          borderRadius: "12px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          fontSize: "14px",
          fontWeight: 600,
          color: canWithdraw ? "#fff" : "text.disabled",
          backgroundColor: canWithdraw
            ? "primary.main"
            : (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)",
          transition: "transform 0.15s ease",
          "&:active": {
            transform: canWithdraw ? "scale(0.985)" : "none",
          },
        }}
      >
        <Icon
          icon="solar:download-minimalistic-bold"
          width={16}
          height={16}
        />
        {t("hero.withdraw")}
      </ButtonBase>

      {/* Прогресс до следующего тира */}
      {nextTier && !isPremium ? (
        <Box sx={{ marginTop: "14px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              marginBottom: "6px",
            }}
          >
            <Typography
              sx={{ fontSize: "12px", opacity: 0.65, fontWeight: 500 }}
            >
              {t("hero.to_tier_short", { name: nextTier.name })}
            </Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
              {referralsTotal}/{nextTier.min_referrals}
              <Typography
                component="span"
                sx={{
                  fontSize: "11px",
                  opacity: 0.5,
                  marginLeft: "4px",
                  fontWeight: 500,
                }}
              >
                {t("hero.invited_short")}
              </Typography>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.round(progressToNext * 100)}
            sx={{
              height: "6px",
              borderRadius: "999px",
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.05)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "primary.main",
                borderRadius: "999px",
              },
            }}
          />
          {remaining > 0 && (
            <Typography
              sx={{
                fontSize: "11px",
                opacity: 0.5,
                marginTop: "6px",
              }}
            >
              {t("hero.remaining_to_next", { count: remaining })}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Icon
            icon="solar:crown-bold"
            width={14}
            height={14}
            color="#3377FF"
          />
          <Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
            {isPremium ? t("hero.max_level_premium") : t("hero.max_level")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
